import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import SellerSidebar from '../SellerSidebar';
import RequirementModal from '../RequirementModal';
import NotificationBell from '../NotificationBell/NotificationBell';
import './SellerDashboardLayout.css';

const API_BASE_URL = '/api';

const SellerDashboardLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, isAuthenticated } = useAuth();
  const [showMobileProfileDropdown, setShowMobileProfileDropdown] = useState(false);
  const [showDesktopProfileDropdown, setShowDesktopProfileDropdown] = useState(false);
  const [isRFQModalOpen, setIsRFQModalOpen] = useState(false);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [sellerLogo, setSellerLogo] = useState(null);
  const desktopProfileRef = useRef(null);

  // Fetch seller logo if not available or truncated
  useEffect(() => {
    const fetchSellerLogo = async () => {
      if (user?.sellerId || user?.sellerProfileId) {
        const sellerId = user.sellerId || user.sellerProfileId;
        try {
          const response = await fetch(`${API_BASE_URL}/sellers/${sellerId}`);
          if (response.ok) {
            const data = await response.json();
            if (data.logo) {
              setSellerLogo(data.logo);
            }
          }
        } catch (error) {
          console.error('Error fetching seller logo:', error);
        }
      }
    };

    // Check if logo needs to be fetched (truncated or missing)
    const currentLogo = user?.companyLogo;
    if (!currentLogo || currentLogo.includes('...truncated')) {
      fetchSellerLogo();
    } else {
      setSellerLogo(currentLogo);
    }
  }, [user]);

  // Fix mobile viewport height (for iOS Safari address bar issue)
  useEffect(() => {
    const setVH = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };
    setVH();
    window.addEventListener('resize', setVH);
    window.addEventListener('orientationchange', setVH);
    return () => {
      window.removeEventListener('resize', setVH);
      window.removeEventListener('orientationchange', setVH);
    };
  }, []);

  // Close mobile sidebar on route change
  useEffect(() => {
    setIsMobileSidebarOpen(false);
  }, [location.pathname]);

  // Lock body scroll when mobile sidebar is open
  useEffect(() => {
    if (isMobileSidebarOpen) {
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    };
  }, [isMobileSidebarOpen]);

  // Close desktop profile dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (desktopProfileRef.current && !desktopProfileRef.current.contains(e.target)) {
        setShowDesktopProfileDropdown(false);
      }
    };
    if (showDesktopProfileDropdown) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showDesktopProfileDropdown]);

  const toggleMobileSidebar = () => {
    setIsMobileSidebarOpen(!isMobileSidebarOpen);
  };

  // Sidebar menu items for mobile drawer
  const sidebarMenuItems = [
    { id: 'dashboard', icon: 'home', label: 'Dashboard', path: '/seller-dashboard' },
    { id: 'products', icon: 'list', label: 'Product Listing', path: '/product-manage' },
    { id: 'rfq', icon: 'bell', label: 'RFQ Manage', path: '/rfq-manage' },
    { id: 'messages', icon: 'chat', label: 'Inqs / Msgs', path: '/messages' },
    { id: 'my-inquiries', icon: 'chat', label: 'My Inquiries', path: '/my-inquiries' },
    { id: 'membership', icon: 'invoice', label: 'Membership Plan', path: '/membership-packages' },
    { id: 'favorite', icon: 'heart', label: 'Saved Suppliers', path: '/favorites-supplier' },
    { id: 'favorite-products', icon: 'heart', label: 'Saved Products', path: '/favorites-products' },
    { id: 'support', icon: 'chat', label: 'Support', path: '/my-support' },
    { id: 'profile', icon: 'user', label: 'Profile', path: '/profile' },
  ];

  const isActivePath = (itemPath, itemId) => {
    if (itemId === 'dashboard' && location.pathname === '/seller-dashboard') return true;
    if (itemId === 'products' && (location.pathname === '/product-manage' || location.pathname === '/product-input')) return true;
    if (itemId === 'profile' && (location.pathname === '/profile' || location.pathname === '/my-profile' || location.pathname === '/supplier-profile')) return true;
    if (itemId === 'favorite' && (location.pathname === '/favorites-supplier' || location.pathname === '/favorites')) return true;
    if (itemId === 'favorite-products' && (location.pathname === '/favorites-products' || location.pathname === '/saved-products')) return true;
    return location.pathname === itemPath;
  };

  const handleLogoClick = () => {
    navigate('/');
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className="seller-dashboard-layout">
      {/* Header */}
      <header className="dashboard-header">
        <div className="header-content">
          {/* Header Left - Logo and Mobile Hamburger */}
          <div className="header-left">
            {/* Mobile Hamburger Button */}
            <button className="mobile-hamburger-btn mobile-only" onClick={toggleMobileSidebar} aria-label="Menu">
              {isMobileSidebarOpen ? (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M18 6L6 18M6 6L18 18" stroke="#1F396C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              ) : (
                <img src="/mobile-icons/hamburger-menu.png" alt="Menu" className="hamburger-icon-img" />
              )}
            </button>

            <img 
              src="/logo.png?v=2" 
              alt="Conflux" 
              className="logo clickable-logo"
              onClick={handleLogoClick}
              title="Go to Home"
            />
          </div>
          
          {/* Desktop Header Right */}
          <div className="header-right desktop-only">
            <NotificationBell />

            <div className="user-profile-wrapper" ref={desktopProfileRef}>
              <div className="user-profile" onClick={() => setShowDesktopProfileDropdown(!showDesktopProfileDropdown)}>
                <div className="user-avatar">
                  {sellerLogo || user?.avatar ? (
                    <img src={sellerLogo || user.avatar} alt="User" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="20" cy="20" r="20" fill="rgba(0,0,0,0.16)"/>
                      <circle cx="20" cy="16" r="6" fill="white"/>
                      <path d="M10 32C10 27.5817 14.4772 24 20 24C25.5228 24 30 27.5817 30 32" fill="white"/>
                    </svg>
                  )}
                </div>
                <div className="user-info">
                  <span className="company-name-header">{user?.companyName || 'My Company'}</span>
                  <div className="user-role">
                    <span>Supplier</span>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ transform: showDesktopProfileDropdown ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>
                      <path d="M12 6.33398L8 10.334L4 6.33398H12Z" fill="#6D6D6D" stroke="#6D6D6D" strokeWidth="2" strokeLinejoin="round"/>
                    </svg>
                  </div>
                </div>
              </div>

              {showDesktopProfileDropdown && (
                <div className="desktop-profile-dropdown">
                  <button className="desktop-dropdown-item" onClick={() => { setShowDesktopProfileDropdown(false); navigate('/profile'); }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21M16 7C16 9.20914 14.2091 11 12 11C9.79086 11 8 9.20914 8 7C8 4.79086 9.79086 3 12 3C14.2091 3 16 4.79086 16 7Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <span>My Profile</span>
                  </button>
                  <button className="desktop-dropdown-item" onClick={() => { setShowDesktopProfileDropdown(false); navigate('/seller-dashboard'); }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <path d="M3 9L12 2L21 9V20C21 20.5304 20.7893 21.0391 20.4142 21.4142C20.0391 21.7893 19.5304 22 19 22H5C4.46957 22 3.96086 21.7893 3.58579 21.4142C3.21071 21.0391 3 20.5304 3 20V9Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M9 22V12H15V22" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <span>Dashboard</span>
                  </button>
                  <button className="desktop-dropdown-item" onClick={() => { setShowDesktopProfileDropdown(false); navigate('/membership-packages'); }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M14 2V8H20M16 13H8M16 17H8M10 9H8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <span>Membership</span>
                  </button>
                  <div className="desktop-dropdown-divider"></div>
                  <button className="desktop-dropdown-item logout-item" onClick={() => { setShowDesktopProfileDropdown(false); handleLogout(); }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <path d="M9 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H9" stroke="#dc3545" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M16 17L21 12L16 7" stroke="#dc3545" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M21 12H9" stroke="#dc3545" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <span>Logout</span>
                  </button>
                </div>
              )}
            </div>
          </div>
          
          {/* Mobile Profile Button */}
          <div className="mobile-profile-wrapper mobile-only">
            <button 
              className="mobile-profile-btn" 
              onClick={() => setShowMobileProfileDropdown(!showMobileProfileDropdown)}
              aria-label="Profile Menu"
            >
              {isAuthenticated && (sellerLogo || user?.avatar) ? (
                <img src={sellerLogo || user.avatar} alt="User" className="mobile-avatar-img" />
              ) : (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21M16 7C16 9.20914 14.2091 11 12 11C9.79086 11 8 9.20914 8 7C8 4.79086 9.79086 3 12 3C14.2091 3 16 4.79086 16 7Z" stroke="#1F396C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              )}
            </button>
            
            {/* Mobile Profile Dropdown */}
            {showMobileProfileDropdown && (
              <>
                <div className="mobile-profile-overlay" onClick={() => setShowMobileProfileDropdown(false)}></div>
                <div className="mobile-profile-dropdown">
                  <div className="mobile-profile-menu">
                    {/* Inquiries */}
                    <button className="mobile-profile-item" onClick={() => { setShowMobileProfileDropdown(false); navigate('/messages'); }}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                        <path d="M4 4H20C21.1 4 22 4.9 22 6V18C22 19.1 21.1 20 20 20H4C2.9 20 2 19.1 2 18V6C2 4.9 2.9 4 4 4Z" stroke="#333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M22 6L12 13L2 6" stroke="#333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      <span>Inquiries</span>
                    </button>
                    
                    {/* Chats */}
                    <button className="mobile-profile-item" onClick={() => { setShowMobileProfileDropdown(false); navigate('/messages'); }}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                        <path d="M21 11.5C21.0034 12.8199 20.6951 14.1219 20.1 15.3C19.3944 16.7118 18.3098 17.8992 16.9674 18.7293C15.6251 19.5594 14.0782 19.9994 12.5 20C11.1801 20.0035 9.87812 19.6951 8.7 19.1L3 21L4.9 15.3C4.30493 14.1219 3.99656 12.8199 4 11.5C4.00061 9.92179 4.44061 8.37488 5.27072 7.03258C6.10083 5.69028 7.28825 4.6056 8.7 3.90003C9.87812 3.30496 11.1801 2.99659 12.5 3.00003H13C15.0843 3.11502 17.053 3.99479 18.5291 5.47089C20.0052 6.94699 20.885 8.91568 21 11V11.5Z" stroke="#333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      <span>Chats</span>
                    </button>
                    
                    {/* Quotations */}
                    <button className="mobile-profile-item" onClick={() => { setShowMobileProfileDropdown(false); navigate('/rfq'); }}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                        <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="#333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M14 2V8H20" stroke="#333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M16 13H8" stroke="#333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M16 17H8" stroke="#333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M10 9H9H8" stroke="#333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      <span>Quotations</span>
                    </button>
                    
                    {/* Favorites */}
                    <button className="mobile-profile-item" onClick={() => { setShowMobileProfileDropdown(false); navigate('/favorites'); }}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                        <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" stroke="#333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      <span>Favorites</span>
                    </button>
                    
                    {/* Follow */}
                    <button className="mobile-profile-item" onClick={() => { setShowMobileProfileDropdown(false); navigate('/following'); }}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                        <path d="M20.84 4.61C20.3292 4.099 19.7228 3.69364 19.0554 3.41708C18.3879 3.14052 17.6725 2.99817 16.95 2.99817C16.2275 2.99817 15.5121 3.14052 14.8446 3.41708C14.1772 3.69364 13.5708 4.099 13.06 4.61L12 5.67L10.94 4.61C9.9083 3.57831 8.50903 2.99871 7.05 2.99871C5.59096 2.99871 4.19169 3.57831 3.16 4.61C2.1283 5.64169 1.54871 7.04097 1.54871 8.5C1.54871 9.95903 2.1283 11.3583 3.16 12.39L4.22 13.45L12 21.23L19.78 13.45L20.84 12.39C21.351 11.8792 21.7563 11.2728 22.0329 10.6054C22.3095 9.93789 22.4518 9.22248 22.4518 8.5C22.4518 7.77752 22.3095 7.0621 22.0329 6.39464C21.7563 5.72718 21.351 5.12075 20.84 4.61Z" stroke="#333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      <span>Follow</span>
                    </button>
                    
                    {/* Post Sourcing Request */}
                    <button className="mobile-profile-item" onClick={() => { setShowMobileProfileDropdown(false); setIsRFQModalOpen(true); }}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                        <path d="M11 4H4C3.46957 4 2.96086 4.21071 2.58579 4.58579C2.21071 4.96086 2 5.46957 2 6V20C2 20.5304 2.21071 21.0391 2.58579 21.4142C2.96086 21.7893 3.46957 22 4 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V13" stroke="#333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M18.5 2.50001C18.8978 2.10219 19.4374 1.87869 20 1.87869C20.5626 1.87869 21.1022 2.10219 21.5 2.50001C21.8978 2.89784 22.1213 3.4374 22.1213 4.00001C22.1213 4.56262 21.8978 5.10219 21.5 5.50001L12 15L8 16L9 12L18.5 2.50001Z" stroke="#333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      <span>Post Sourcing Request</span>
                    </button>
                    
                    {/* Logout - Last item */}
                    <button className="mobile-profile-logout" onClick={() => { setShowMobileProfileDropdown(false); handleLogout(); }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                        <path d="M9 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H9" stroke="#dc3545" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M16 17L21 12L16 7" stroke="#dc3545" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M21 12H9" stroke="#dc3545" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      Logout
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Mobile Sidebar Overlay */}
      <div 
        className={`mobile-sidebar-overlay ${isMobileSidebarOpen ? 'active' : ''}`} 
        onClick={toggleMobileSidebar}
      />

      {/* Mobile Sidebar Drawer */}
      <nav className={`mobile-sidebar-drawer ${isMobileSidebarOpen ? 'open' : ''}`}>
        <div className="mobile-drawer-header">
          <img 
            src="/logo.png?v=2" 
            alt="Conflux" 
            className="drawer-logo"
            onClick={() => { setIsMobileSidebarOpen(false); navigate('/'); }}
          />
          <button className="drawer-close-btn" onClick={toggleMobileSidebar}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18M6 6L18 18" stroke="#1F396C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>

        {/* User Info in Drawer */}
        {isAuthenticated && (
          <div className="mobile-drawer-user">
            <div className="drawer-user-avatar">
              {sellerLogo || user?.avatar ? (
                <img src={sellerLogo || user.avatar} alt="User" />
              ) : (
                <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
                  <circle cx="20" cy="20" r="20" fill="#1F396C"/>
                  <circle cx="20" cy="16" r="6" fill="white"/>
                  <path d="M10 32C10 27.5817 14.4772 24 20 24C25.5228 24 30 27.5817 30 32" fill="white"/>
                </svg>
              )}
            </div>
            <div className="drawer-user-info">
              <span className="drawer-company-name">{user?.companyName || 'My Company'}</span>
              <span className="drawer-user-role">Supplier</span>
            </div>
          </div>
        )}

        {/* Navigation Menu */}
        <div className="mobile-drawer-nav">
          {sidebarMenuItems.map((item) => (
            <Link
              key={item.id}
              to={item.path}
              className={`mobile-drawer-item ${isActivePath(item.path, item.id) ? 'active' : ''}`}
              onClick={() => setIsMobileSidebarOpen(false)}
            >
              <span className="drawer-item-icon">
                {item.id === 'dashboard' && (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M3 9L12 2L21 9V20C21 20.5304 20.7893 21.0391 20.4142 21.4142C20.0391 21.7893 19.5304 22 19 22H5C4.46957 22 3.96086 21.7893 3.58579 21.4142C3.21071 21.0391 3 20.5304 3 20V9Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/><path d="M9 22V12H15V22" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                )}
                {item.id === 'products' && (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M4 6.25H20M4 12H15M4 17H9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                )}
                {item.id === 'rfq' && (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M18 8C18 6.4087 17.3679 4.88258 16.2426 3.75736C15.1174 2.63214 13.5913 2 12 2C10.4087 2 8.88258 2.63214 7.75736 3.75736C6.63214 4.88258 6 6.4087 6 8C6 15 3 17 3 17H21C21 17 18 15 18 8Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/><path d="M13.73 21C13.5542 21.3031 13.3019 21.5547 12.9982 21.7295C12.6946 21.9044 12.3504 21.9965 12 21.9965C11.6496 21.9965 11.3054 21.9044 11.0018 21.7295C10.6982 21.5547 10.4458 21.3031 10.27 21" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                )}
                {item.id === 'messages' && (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M21 11.5C21.0034 12.8199 20.6951 14.1219 20.1 15.3C19.3944 16.7118 18.3098 17.8992 16.9674 18.7293C15.6251 19.5594 14.0782 19.9994 12.5 20C11.1801 20.0035 9.87812 19.6951 8.7 19.1L3 21L4.9 15.3C4.30493 14.1219 3.99656 12.8199 4 11.5C4.00061 9.92179 4.44061 8.37488 5.27072 7.03258C6.10083 5.69028 7.28825 4.6056 8.7 3.90003C9.87812 3.30496 11.1801 2.99659 12.5 3.00003H13C15.0843 3.11502 17.053 3.99479 18.5291 5.47089C20.0052 6.94699 20.885 8.91568 21 11V11.5Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                )}
                {item.id === 'membership' && (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/><path d="M14 2V8H20M16 13H8M16 17H8M10 9H8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                )}
                {item.id === 'favorite' && (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M20.84 4.61C20.3292 4.099 19.7228 3.69364 19.0554 3.41708C18.3879 3.14052 17.6725 2.99817 16.95 2.99817C16.2275 2.99817 15.5121 3.14052 14.8446 3.41708C14.1772 3.69364 13.5708 4.099 13.06 4.61L12 5.67L10.94 4.61C9.9083 3.57831 8.50903 2.99871 7.05 2.99871C5.59096 2.99871 4.19169 3.57831 3.16 4.61C2.1283 5.64169 1.54871 7.04097 1.54871 8.5C1.54871 9.95903 2.1283 11.3583 3.16 12.39L12 21.23L20.84 12.39C21.351 11.8792 21.7563 11.2728 22.033 10.6054C22.3095 9.93789 22.4518 9.22248 22.4518 8.5C22.4518 7.77752 22.3095 7.0621 22.033 6.39464C21.7563 5.72718 21.351 5.12075 20.84 4.61Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                )}
                {item.id === 'profile' && (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21M16 7C16 9.20914 14.2091 11 12 11C9.79086 11 8 9.20914 8 7C8 4.79086 9.79086 3 12 3C14.2091 3 16 4.79086 16 7Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                )}
              </span>
              <span className="drawer-item-label">{item.label}</span>
            </Link>
          ))}
        </div>

        {/* Logout Button */}
        <div className="mobile-drawer-footer">
          <button className="mobile-drawer-logout" onClick={() => { setIsMobileSidebarOpen(false); handleLogout(); }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M9 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H9" stroke="#dc3545" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M16 17L21 12L16 7" stroke="#dc3545" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M21 12H9" stroke="#dc3545" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span>Logout</span>
          </button>
        </div>
      </nav>

      <div className="dashboard-body">
        <SellerSidebar />
        <main className="dashboard-main-content">
          {children}
        </main>
      </div>
      
      {/* RFQ Modal */}
      {isRFQModalOpen && (
        <RequirementModal onClose={() => setIsRFQModalOpen(false)} />
      )}
    </div>
  );
};

export default SellerDashboardLayout;
